import type { GlobalSingleRelationFieldId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { WorkspaceCollectionsMap } from '#layers/workspace/types/collections';
import { UserStatus } from '#layers/base/public';
import { CREATE_MODE_ITEM_ID, DisplayTemplateAstType, fieldIdToDisplayTemplateAst, findCollectionPrimitiveFieldByApiName, findCollectionRelationFieldByApiName, getFieldPaths, getJunctionTargetLeg, serializeFilter } from '#layers/data-model/public';
import { useFileCollection } from '#layers/workspace/composables/use-file-collection';

const MONOSPACE_FILES_THUMBNAIL_ID = '9b3ea5f2-e43b-44d0-83f3-e2d97dfff065' as GlobalSingleRelationFieldId;

/**
 * The workspace's single public role, provisioned at workspace creation under a fixed id.
 *
 * It governs unauthenticated API access, so the engine refuses to delete it or attach it to a
 * user; the UI keys off this id to keep those actions out of reach.
 */
export const PUBLIC_ROLE_ID = '00000000-0000-0000-0000-000000000001';

/** The workspace's protected administrator role, provisioned under a fixed id. */
export const ADMIN_ROLE_ID = '00000000-0000-0000-0000-000000000002';

/**
 * Declares the relation field named by `relationFieldApiName` on `collection` as one endpoint of an
 * M2M relation running through a junction collection.
 *
 * Studio recognises an M2M by `oppositeJunctionRelationFieldId` on the endpoint meta, which the data
 * model UI writes when a user builds one. System relations never go through that flow, so their
 * endpoints are declared here; without it the endpoint reads as a plain to-many onto the junction
 * and the query fetches neither the junction's key nor the target's display fields.
 */
function m2mEndpointMeta(collection: ResolvedMonospaceCollection, relationFieldApiName: string) {
	const relationField = findCollectionRelationFieldByApiName(collection, relationFieldApiName);
	const targetLeg = relationField && getJunctionTargetLeg(relationField);

	if (!targetLeg) {
		console.warn(`Could not resolve the junction leg for ${collection.apiName}.${relationFieldApiName}; it will not be treated as an M2M relation.`);
		return {};
	}

	return { oppositeJunctionRelationFieldId: targetLeg.id };
}

export const WORKSPACE_COLLECTIONS: WorkspaceCollectionsMap = {
	MonospaceFile: {
		routes: {
			listing: {
				name: 'assets.library',
			},
			detail: (key) => {
				if (key === CREATE_MODE_ITEM_ID) {
					return {
						name: 'assets.library',
					};
				}

				return {
					name: 'assets.detail',
					params: {
						id: key?.id,
					},
				};
			},
		},
		schema: (collection) => {
			const filenameFieldId = findCollectionPrimitiveFieldByApiName(collection, 'fileName')?.id;
			return {
				id: {
					meta: {
						isHidden: true,
					},
				},
				// Editable content first (a0-a3), then read-only technical metadata (a4-a6).
				title: {
					meta: {
						sort: 'a1',
					},
				},
				description: {
					meta: {
						inputId: '$builtin:InputTextarea',
						sort: 'a2',
					},
				},
				tags: {
					meta: {
						inputId: '$builtin:InputTags',
						sort: 'a3',
					},
				},
				fileName: {
					meta: {
						inputOptions: {
							disabled: true,
						},
						sort: 'a4',
					},
				},
				mediaType: {
					meta: {
						displayId: '$builtin:DisplayFormattedValue',
						displayOptions: {
							fontFamily: 'mono',
						},
						inputOptions: {
							disabled: true,
						},
						sort: 'a5',
					},
				},
				fileSize: {
					meta: {
						inputId: '$builtin:DisplayFileSize',
						displayId: '$builtin:DisplayFileSize',
						sort: 'a6',
					},
				},
				// Engine-managed timestamps: surfaced through sorting and filtering, not the form.
				createdAt: {
					meta: {
						isHidden: true,
						displayId: '$builtin:DisplayFormattedDateTime',
						displayOptions: {
							displayMode: 'relative',
						},
					},
				},
				updatedAt: {
					meta: {
						isHidden: true,
						displayId: '$builtin:DisplayFormattedDateTime',
						displayOptions: {
							displayMode: 'relative',
						},
					},
				},
				$meta: {
					displayName: '$t:common.assets',
					displayTemplate: {
						type: DisplayTemplateAstType.Doc,
						content: [
							{
								type: DisplayTemplateAstType.Ref,
								attrs: {
									fieldId: MONOSPACE_FILES_THUMBNAIL_ID,
								},
							},

							...(filenameFieldId
								? [
										{
											type: DisplayTemplateAstType.Text,
											// Acts as separator between the thumbnail and field id
											// TODO: Solve this properly, e.g. a separator node
											text: '  ',
										},
										{
											type: DisplayTemplateAstType.Ref,
											attrs: {
												fieldId: filenameFieldId,
											},
										},
									] as const
								: []),
						],
					},
				},
				$extend({ addSingleRelationField }) {
					addSingleRelationField('$thumbnail', 'MonospaceFile', {
						id: MONOSPACE_FILES_THUMBNAIL_ID,
						fields: {
							'MonospaceFile.id': 'MonospaceFile.id',
						},
						meta: {
							displayName: '$t:common.preview',
							inputId: '$builtin:InputFileM2O',
							inputOptions: {
								showRemove: false,
							},
							sort: 'a0',
						},
					});
				},
			};
		},
		listing: {
			defaultView: () => {
				const { titleField, thumbnailField, mediaTypeField, createdAtField } = useFileCollection();

				return {
					layoutId: '$builtin:LayoutCards',
					displayOptions: {
						titleTemplate: titleField.value ? fieldIdToDisplayTemplateAst(titleField.value.id) : null,
						descriptionTemplate: mediaTypeField.value ? fieldIdToDisplayTemplateAst(mediaTypeField.value.id) : null,
						thumbnailField: thumbnailField.value?.id,
						// Omitted rather than left undefined so the layout keeps its primary-key fallback.
						...(createdAtField.value && {
							sorting: [{ fieldId: createdAtField.value.id, direction: 'desc' as const }],
						}),
					},
				};
			},
		},
	},
	MonospacePolicy: {
		routes: {
			listing: {
				name: 'workspace.settings.access.policies.listing',
			},
			detail: (key) => ({
				name: 'workspace.settings.access.policies.detail',
				params: {
					policy: key === CREATE_MODE_ITEM_ID ? CREATE_MODE_ITEM_ID : key?.id,
				},
			}),
		},
		listing: {
			defaultView: (collection) => ({
				layoutId: '$builtin:LayoutTable',
				displayOptions: {
					visibleColumnPaths: getFieldPaths(collection, ['name']),
				},
			}),
		},
		schema: (collection) => ({
			$meta: {
				displayName: '$t:common.policies',
				displayTemplate: '{{ name }}',
			},
			id: {
				meta: {
					isHidden: true,
				},
			},
			name: {
				meta: {
					inputId: '$builtin:InputTranslationKey',
					sort: 'a0',
				},
			},
			content: {
				meta: {
					displayName: '',
					inputId: '$builtin:InputPolicyDocument',
					sort: 'a1',
				},
			},
			filterFields: {
				meta: {
					isHidden: true,
				},
			},
			roles: {
				meta: {
					isHidden: true,
					...m2mEndpointMeta(collection, 'roles'),
				},
			},
			isProtected: {
				meta: {
					isHidden: true,
				},
			},
		}),
	},
	MonospaceRole: {
		routes: {
			listing: {
				name: 'workspace.settings.access.roles.listing',
			},
			detail: (key) => ({
				name: 'workspace.settings.access.roles.detail',
				params: {
					id: key === CREATE_MODE_ITEM_ID ? CREATE_MODE_ITEM_ID : key?.id,
				},
			}),
		},
		schema: (collection) => ({
			$meta: {
				displayName: '$t:common.roles',
				displayTemplate: '{{ name }}',
			},
			id: {
				meta: {
					isHidden: true,
				},
			},
			name: {
				meta: {
					inputId: '$builtin:InputTranslationKey',
					displayId: '$builtin:DisplayRoleName',
				},
			},
			policies: {
				meta: {
					inputId: '$builtin:InputPolicyAssignment',
					...m2mEndpointMeta(collection, 'policies'),
				},
			},
			users: {
				meta: {
					isHidden: true,
					...m2mEndpointMeta(collection, 'users'),
				},
			},
		}),
		listing: {
			defaultView: (collection) => ({
				layoutId: '$builtin:LayoutTable',
				displayOptions: {
					visibleColumnPaths: getFieldPaths(collection, ['name', 'parent', 'policies']),
					// Ascending id pins the public role to the top: its id is fixed at all-zeros-but-one,
					// which sorts ahead of every generated role id.
					sorting: [
						{ fieldId: findCollectionPrimitiveFieldByApiName(collection, 'id')?.id, direction: 'asc' },
					],
				},
			}),
		},
	},
	MonospaceSystemUser: {
		routes: {
			listing: {
				name: 'workspace.settings.access.members.listing',
			},
			detail: (key) => ({
				name: 'workspace.settings.access.members.detail',
				params: {
					id: key === CREATE_MODE_ITEM_ID ? CREATE_MODE_ITEM_ID : key?.id,
				},
			}),
		},
		listing: {
			defaultView: (collection) => ({
				layoutId: '$builtin:LayoutTable',
				// Suspension is an organization concern, the workspace list hides suspended members by default.
				filter: serializeFilter({ _and: [{ status: { _eq: UserStatus.Active } }] }, collection),
				displayOptions: {
					visibleColumnPaths: getFieldPaths(collection, ['fullName', 'email', 'roles', 'lastActivityAt']),
				},
			}),
		},
		schema: (collection) => ({
			$meta: {
				displayName: '$t:common.members',
				// TODO update to use profile picture
				displayTemplate: '{{ fullName }}',
			},
			id: {
				meta: {
					isHidden: true,
					typeTemplateId: 'identifier',
				},
			},
			email: {
				meta: {
					inputOptions: {
						disabled: true,
					},
				},
			},
			fullName: {
				meta: {
					// TODO rework display names such that they can be translated from placeholders
					// displayName: '$t:common.name',
					inputOptions: {
						disabled: true,
					},
				},
			},
			roles: {
				meta: {
					inputId: '$builtin:InputRoleAssignment',
					...m2mEndpointMeta(collection, 'roles'),
				},
			},
			avatarId: {
				meta: {
					isHidden: true,
				},
			},
			storageValues: {
				meta: {
					isHidden: true,
				},
			},
			isService: {
				meta: {
					isHidden: true,
				},
			},
			serviceOwnerId: {
				meta: {
					isHidden: true,
				},
			},
			appearance: {
				meta: {
					isHidden: true,
				},
			},
			status: {
				meta: {
					isHidden: true,
					displayId: '$builtin:DisplayLabels',
					displayName: '$t:pages.settings.access.pages.members.table.status',
					displayOptions: {
						displayStyle: 'dot-with-label',
						convertToTitleCase: false,
						options: [
							{
								value: UserStatus.Active,
								label: '$t:pages.settings.access.pages.members.table.status_active',
								icon: 'lucide:user-check',
								color: 'var(--ui-success)',
							},
							{
								value: UserStatus.Suspended,
								label: '$t:pages.settings.access.pages.members.table.status_suspended',
								icon: 'lucide:user-x',
								color: 'var(--ui-error)',
							},
						],
					},
				},
			},
			lastActivityAt: {
				meta: {
					displayName: '$t:pages.settings.access.pages.members.table.last_activity',
					isHidden: true,
					displayId: '$builtin:DisplayFormattedDateTime',
					displayOptions: {
						displayMode: 'relative',
					},
				},
			},
		}),
	},
	MonospaceSpace: {
		schema: {
			$meta: {
				displayTemplate: '{{ title }}',
			},
		},
		listing: {
			defaultView: (collection) => ({
				layoutId: '$builtin:LayoutTable',
				displayOptions: {
					visibleColumnPaths: getFieldPaths(collection, ['title', 'description']),
				},
			}),
		},
	},
	MonospaceUserRole: {
		schema: {
			$meta: {
				displayTemplate: '{{ role }}',
			},
		},
	},
	MonospaceRolePolicy: {
		schema: {
			$meta: {
				displayTemplate: '{{ policy }}',
			},
		},
	},
} as const;
